import * as Sentry from "@sentry/react";

import {getSentryContext, setSentryContext} from "config/sentryContext";
import {getSessionId} from "utils/sessionId";
import {ErrorType} from "config/errors";
import config from "./config";

// Workspace-owned sentry. The monolith's `config/sentry.ts` relative-imports its own
// `config/config.ts`, which reads `process.env.REACT_APP_*` directly and isn't safe
// under Vite, so the workspace mirrors its API surface here instead. No react-router
// integration (this app has no router — single-view form), so tracing is unrouted.

function isIgnoredError(error: unknown): boolean {
    if (!error) return false;

    const name = error instanceof Error ? error.name : (error as {name?: string})?.name;

    return Boolean(name && config.sentryIgnoredErrors.includes(name));
}

function shouldIgnoreEvent(event: Sentry.Event): boolean {
    const exceptions = event.exception?.values ?? [];
    return exceptions.some((ex) => ex?.type && config.sentryIgnoredErrors.includes(ex.type));
}

export const initSentry = (): void => {
    if (!config.sentryDsn) {
        return;
    }

    setSentryContext({app_mode: "webform"});

    Sentry.init({
        dsn: config.sentryDsn,
        environment: config.sentryEnvironment,
        enabled: config.sentryEnvironment !== "development",
        tracesSampleRate: Number(config.sentryTracesSampleRate),
        sampleRate: Number(config.sentrySampleRate),
        // Setting this option to true will send default PII data to Sentry.
        // For example, automatic IP address collection on events.
        sendDefaultPii: false,
        beforeSend: (event) => (shouldIgnoreEvent(event) ? null : event),
        integrations: [Sentry.browserSessionIntegration({lifecycle: "page"}), Sentry.browserTracingIntegration()],
        normalizeDepth: 10,
    });

    Sentry.addEventProcessor((event) => {
        const context = getSentryContext();
        event.tags = {
            ...event.tags,
            app_mode: context.app_mode,
            session_id: getSessionId(),
            // Distinguishes workspace-origin events from the monolith's in the shared
            // Sentry project. Absence of this tag means the event came from the monolith.
            source: "workspace",
        };
        if (context.parent_type) {
            event.tags = {...event.tags, parent_type: context.parent_type};
        }
        return event;
    });
};

export const notify = (message: string, errorType: ErrorType | string, metadata?: Record<string, unknown>): void => {
    const error = new Error(message);
    error.name = errorType;

    if (isIgnoredError(metadata?.error)) {
        return;
    }

    if (import.meta.env.MODE === "test") {
        console.error("[Sentry]", errorType, message, metadata);
    }

    Sentry.withScope((scope) => {
        if (metadata) {
            scope.setExtras(metadata);
        }
        scope.setFingerprint([errorType, "{{ default }}"]);
        Sentry.captureException(error);
    });
};

export const setSentryUser = (userId: number | undefined, appMode: string): void => {
    if (!config.sentryDsn) return;

    if (userId === undefined) {
        Sentry.setUser(null);
        return;
    }
    Sentry.setUser({
        id: `${appMode}-${userId}`,
    });
};

export const addBreadcrumb = (
    message: string,
    level: Sentry.SeverityLevel = "debug",
    data?: Record<string, unknown>,
): void => {
    Sentry.addBreadcrumb({
        message,
        level,
        data,
    });
};
